import React, { useMemo, useState } from "react";
import { Tag, Badge, Tooltip, Space, Popover, Timeline, Progress, Row, Col, Card, Divider, Button } from "antd";
import { ClockCircleOutlined, ArrowRightOutlined, RocketOutlined, FieldTimeOutlined, CheckCircleOutlined, ExclamationCircleOutlined, WarningOutlined, LoadingOutlined, SyncOutlined, FlagOutlined, EditOutlined, StopOutlined, InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import FlightTimeDisplay from "../../../../components/DateTimeDisplay";
import FlightInfoEditor from "./FlightInfoEditor";
import { FLIGHT_STATUS, FLIGHT_STATUS_LABELS, FLIGHT_STATUS_COLORS, FLIGHT_STATUS_ICONS } from "../constants/flightConstants";
import PermissionGuard from "../../../../components/auth/PermissionGuard";
import FlightChangesSummary from "./FlightChangesSummary";
import { resolveTime } from "../../../../utils/resolveTime";

// 扩展dayjs
dayjs.extend(utc);
dayjs.extend(duration);
dayjs.extend(relativeTime);

/**
 * 增强型航班时间组件
 * 提供更直观的时间信息展示，根据航班状态智能显示最相关的时间信息
 * 支持轮档时间和过站时间计算，符合航空业OCC标准操作程序
 *
 * @param {Object} props
 * @param {Object} props.flight - 当前航班数据
 * @param {Object} props.airportsMap - 机场信息映射，用于时区转换
 * @param {Array} props.allFlights - 所有航班数据，用于计算过站时间
 * @param {Function} props.onFlightInfoEditSuccess - 航班信息编辑成功后的回调函数
 * @param {React.ReactNode} [props.triggerComponent] - 可选的触发 Popover 的组件
 * @param {boolean} [props.editable=true] - 是否允许编辑航班信息
 */
const EnhancedFlightTimes = ({ flight, airportsMap = {}, allFlights = [], onFlightInfoEditSuccess, triggerComponent, showAsModal = false, editable = true }) => {
    // 添加 Popover 显隐状态
    const [popoverVisible, setPopoverVisible] = useState(false);

    // 计算时间差异（分钟）
    const calculateTimeDiff = (time1, time2) => {
        if (!time1 || !time2) return null;
        return dayjs(time1).diff(dayjs(time2), "minute");
    };

    // 直接使用后端返回的轮档时间（分钟数）
    const blockTime = useMemo(() => {
        if (!flight) return null;

        // 直接使用后端返回的 block_time_minutes 字段
        return flight.block_time_minutes || null;
    }, [flight]);

    // 早期返回处理
    if (!flight) return null;

    const { id, std, sta, etd, eta, atd, ata, departure_airport, arrival_airport, status, aircraft_reg, delay_code, delay_reason, stc_type, min_ground_time_minutes, actual_ground_time_minutes } = flight;

    // 降级取值：展示用（settlement 优先），延误计算仍用 MVT 原值（FR-002）
    const resolvedAtd = resolveTime(flight, "atd");
    const resolvedAta = resolveTime(flight, "ata");
    const displayAtd = resolvedAtd.value;
    const displayAta = resolvedAta.value;
    const atdSource = resolvedAtd.source;
    const ataSource = resolvedAta.source;

    // 安全检查，如果没有std/sta则显示提示
    if (!std || !sta) return <span style={{ color: "#ff4d4f" }}>无计划时间</span>;

    // 延误计算用 MVT 原值（FR-002）
    const stdToEtdDiff = etd ? calculateTimeDiff(etd, std) : 0;
    const staToEtaDiff = eta ? calculateTimeDiff(eta, sta) : 0;
    const stdToAtdDiff = atd ? calculateTimeDiff(atd, std) : 0;
    const staToAtaDiff = ata ? calculateTimeDiff(ata, sta) : 0;

    // 简化：直接返回后端 status (如果有效)，不再做前端映射
    const getFlightPhase = () => {
        // 检查 status 是否是有效的 FLIGHT_STATUS 值
        if (Object.values(FLIGHT_STATUS).includes(status)) {
            return status;
        }
        // 如果后端状态未知或为空，则默认为 SCHEDULED
        console.warn(`Unknown flight status received: ${status}, defaulting to SCHEDULED.`);
        return FLIGHT_STATUS.SCHEDULED;
    };

    const flightPhase = getFlightPhase();

    // 获取阶段对应的显示信息 - 直接使用 flightPhase (后端原始状态)
    const getPhaseInfo = (phase) => {
        const baseStatus = phase;

        // 其他状态直接从常量取
        return {
            color: FLIGHT_STATUS_COLORS[baseStatus] || FLIGHT_STATUS_COLORS.default,
            icon: FLIGHT_STATUS_ICONS[baseStatus] || FLIGHT_STATUS_ICONS.default, // Constants 文件需要包含所有状态的图标
            label: FLIGHT_STATUS_LABELS[baseStatus] || baseStatus,
        };
    };

    const phaseInfo = getPhaseInfo(flightPhase);

    // 轮档时间的格式化 (HH:mm)
    const formatBlockTimeDuration = (minutes) => {
        if (minutes === null || minutes === undefined || minutes < 0) return null;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        const paddedHours = String(hours).padStart(2, "0");
        const paddedMins = String(mins).padStart(2, "0");
        return `${paddedHours}:${paddedMins}`;
    };

    // 过站时间的格式化 (Xh Ym, m=0时不显示)
    const formatGroundTimeDuration = (minutes) => {
        if (minutes === null || minutes === undefined) return null;

        const isNegative = minutes < 0;
        const absMinutes = Math.abs(minutes);
        const hours = Math.floor(absMinutes / 60);
        const mins = absMinutes % 60;

        let formattedDuration = "";

        if (hours > 0) {
            formattedDuration += `${hours}h`;
            if (mins > 0) {
                formattedDuration += ` ${mins}m`;
            }
        } else if (mins > 0) {
            formattedDuration = `${mins}m`;
        } else if (hours === 0 && mins === 0) {
            // 如果刚好是0分钟，显示 '0m'
            formattedDuration = "0m";
        }
        // else: 如果 minutes 为负数且刚好是整小时(如-120), hours > 0, mins = 0, formattedDuration会是 '-Xh'
        // 如果 minutes 为0， formattedDuration 是 '0m'

        return isNegative && formattedDuration !== "0m" ? `-${formattedDuration}` : formattedDuration;
    };

    // 根据时间差生成标签颜色
    const getDelayTagColor = (diff) => {
        if (!diff) return "default";
        if (diff <= 0) return "success";
        if (diff <= 15) return "default";
        if (diff <= 30) return "warning";
        return "error";
    };

    // 根据航班阶段显示最关键的时间信息
    const getPrimaryTimeDisplay = () => {
        // 控制可见性条件保持不变，依赖后端原始状态 flightPhase
        // const showEtd = etd && flightPhase === FLIGHT_STATUS.DELAYED;
        // const showEta = eta && (flightPhase === FLIGHT_STATUS.DEPARTED || flightPhase === FLIGHT_STATUS.DELAYED || staToEtaDiff > 0);
        const showEtd = !!etd; // 如果etd有值，则为true
        const showEta = !!eta; // 如果eta有值，则为true
        const showAtdAta = displayAtd && (flightPhase === FLIGHT_STATUS.DEPARTED || flightPhase === FLIGHT_STATUS.COMPLETED);

        // 调整 getBadgeStatus，处理所有后端状态
        const getBadgeStatus = (phase) => {
            switch (phase) {
                case FLIGHT_STATUS.COMPLETED:
                case FLIGHT_STATUS.COMPLETED:
                    return "success";
                case FLIGHT_STATUS.DEPARTED:
                    return "processing";
                case FLIGHT_STATUS.DELAYED:
                    return "warning";
                case FLIGHT_STATUS.CANCELLED:
                case FLIGHT_STATUS.PLAN_CANCELLED:
                case FLIGHT_STATUS.DIVERTED:
                case FLIGHT_STATUS.REPLACED:
                    return "error";
                case FLIGHT_STATUS.SCHEDULED:
                default:
                    return "default";
            }
        };

        return (
            <Row gutter={[0, 0]} style={{ width: "100%", marginBottom: 0 }}>
                {/* 起飞时间行 */}
                <Col span={12} style={{ borderRight: "1px dashed #d9d9d9", paddingRight: 4, lineHeight: "16px" }}>
                    {/* STD - 计划起飞时间 */}
                    {!showEtd && (
                        <Row align="right" style={{ marginBottom: "2px", minHeight: "16px" }}>
                            <Col span={6}>
                                <span style={{ fontSize: "11px" }}>STD</span>
                            </Col>
                            <Col span={18} style={{ textAlign: "right" }}>
                                <FlightTimeDisplay utcTime={std} airportCode={departure_airport} airportsMap={airportsMap} format="HH:mm" style={{ fontSize: "11px" }} />
                            </Col>
                        </Row>
                    )}
                    {/* ETD - 预计起飞时间 */}
                    {showEtd && (
                        <Row align="right" style={{ marginBottom: "2px", minHeight: "16px" }}>
                            <Col span={6}>
                                <span style={{ fontSize: "11px" }}>ETD</span>
                            </Col>
                            <Col span={18} style={{ textAlign: "right" }}>
                                {stdToEtdDiff !== 0 && (
                                    <Tag color={getDelayTagColor(stdToEtdDiff)} style={{ fontSize: "9px", lineHeight: "12px", height: "14px", padding: "0 2px", marginRight: 6 }}>
                                        {stdToEtdDiff > 0 ? `+${stdToEtdDiff}m` : `${stdToEtdDiff}m`}
                                    </Tag>
                                )}
                                <FlightTimeDisplay utcTime={etd} airportCode={departure_airport} airportsMap={airportsMap} format="HH:mm" style={{ fontSize: "11px" }} />
                            </Col>
                        </Row>
                    )}
                    {/* ATD - 实际起飞时间（降级展示） */}
                    {showAtdAta && (
                        <Row align="right" style={{ minHeight: "16px" }}>
                            <Col span={5}>
                                <span style={{ fontSize: "11px" }}>ATD</span>
                            </Col>
                            <Col span={19} style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                {stdToAtdDiff !== 0 && (
                                    <Tag color={getDelayTagColor(stdToAtdDiff)} style={{ fontSize: "9px", lineHeight: "12px", height: "14px", padding: "0 2px", marginRight: 2 }}>
                                        {stdToAtdDiff > 0 ? `+${stdToAtdDiff}m` : `${stdToAtdDiff}m`}
                                    </Tag>
                                )}
                                <FlightTimeDisplay utcTime={displayAtd} airportCode={departure_airport} airportsMap={airportsMap} format="HH:mm" style={{ fontSize: "11px" }} />
                                <Tag color={atdSource === "settlement" ? "blue" : "default"} style={{ fontSize: "8px", lineHeight: "12px", height: "14px", padding: "0 2px", marginLeft: 1, marginRight: 0 }}>{atdSource === "settlement" ? "结" : "运"}</Tag>
                            </Col>
                        </Row>
                    )}
                </Col>

                {/* 到达时间行 */}
                <Col span={12} style={{ paddingLeft: 4, lineHeight: "16px" }}>
                    {/* STA - 计划到达时间 */}
                    {!showEta && (
                        <Row align="middle" style={{ marginBottom: "2px", minHeight: "16px" }}>
                            <Col span={6}>
                                <span style={{ fontSize: "11px" }}>STA</span>
                            </Col>
                            <Col span={18} style={{ textAlign: "right" }}>
                                <FlightTimeDisplay utcTime={sta} airportCode={arrival_airport} baseUtcTime={std} airportsMap={airportsMap} format="HH:mm" style={{ fontSize: "11px" }} />
                            </Col>
                        </Row>
                    )}

                    {/* ETA - 预计到达时间 */}
                    {showEta && (
                        <Row align="middle" style={{ marginBottom: "2px", minHeight: "16px" }}>
                            <Col span={6}>
                                <span style={{ fontSize: "11px" }}>ETA</span>
                            </Col>
                            <Col span={18} style={{ textAlign: "right" }}>
                                {staToEtaDiff !== 0 && (
                                    <Tag color={getDelayTagColor(staToEtaDiff)} style={{ fontSize: "9px", lineHeight: "12px", height: "14px", padding: "0 2px", marginRight: 6 }}>
                                        {staToEtaDiff > 0 ? `+${staToEtaDiff}m` : `${staToEtaDiff}m`}
                                    </Tag>
                                )}
                                <FlightTimeDisplay utcTime={eta} airportCode={arrival_airport} baseUtcTime={etd || std} airportsMap={airportsMap} format="HH:mm" style={{ fontSize: "11px" }} />
                            </Col>
                        </Row>
                    )}

                    {/* ATA - 实际到达时间（降级展示） */}
                    {showAtdAta && (
                        <Row align="middle" style={{ minHeight: "16px" }}>
                            <Col span={5}>
                                <span style={{ fontSize: "11px" }}>ATA</span>
                            </Col>
                            <Col span={19} style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                {staToAtaDiff !== 0 && (
                                    <Tag color={getDelayTagColor(staToAtaDiff)} style={{ fontSize: "9px", lineHeight: "12px", height: "14px", padding: "0 2px", marginRight: 2 }}>
                                        {staToAtaDiff > 0 ? `+${staToAtaDiff}m` : `${staToAtaDiff}m`}
                                    </Tag>
                                )}
                                <FlightTimeDisplay utcTime={displayAta} airportCode={arrival_airport} baseUtcTime={displayAtd || std} airportsMap={airportsMap} format="HH:mm" style={{ fontSize: "11px" }} />
                                <Tag color={ataSource === "settlement" ? "blue" : "default"} style={{ fontSize: "8px", lineHeight: "12px", height: "14px", padding: "0 2px", marginLeft: 1, marginRight: 0 }}>{ataSource === "settlement" ? "结" : "运"}</Tag>
                            </Col>
                        </Row>
                    )}
                </Col>

                {/* 状态和时间指标行 - 使用更新后的 getBadgeStatus 和 phaseInfo */}
                <Col span={24}>
                    <Row
                        align="middle"
                        style={{
                            borderTop: "1px dashed #f0f0f0",
                            paddingTop: 2,
                            lineHeight: "14px",
                        }}
                    >
                        <Col span={6}>
                            <Space size={4}>
                                <Badge status={getBadgeStatus(flightPhase)} text={<span style={{ fontSize: "10px", color: phaseInfo.color }}>{phaseInfo.label}</span>} />
                            </Space>
                        </Col>
                        {/* 轮档和过站时间对齐显示，保证在所有行中位置一致 */}
                        <Col span={18} style={{ textAlign: "right" }}>
                            <Row justify="end">
                                <Col span={9} style={{ textAlign: "left", whiteSpace: "nowrap" }}>
                                    {blockTime !== null ? (
                                        <Tooltip title="轮档时间">
                                            <span style={{ color: "#888", fontSize: "10px", whiteSpace: "nowrap" }}>
                                                <FieldTimeOutlined style={{ marginRight: 1 }} />
                                                {formatBlockTimeDuration(blockTime)}
                                            </span>
                                        </Tooltip>
                                    ) : null}
                                </Col>
                                <Col span={9} style={{ textAlign: "left", whiteSpace: "nowrap" }}>
                                    {actual_ground_time_minutes !== null ? (
                                        <Tooltip title={`过站时间，最小过站时间：${formatGroundTimeDuration(min_ground_time_minutes)}`}>
                                            <span style={{ color: "#888", fontSize: "10px", whiteSpace: "nowrap" }}>
                                                <ClockCircleOutlined style={{ marginRight: 1 }} />
                                                {formatGroundTimeDuration(actual_ground_time_minutes)}
                                                {actual_ground_time_minutes !== null && min_ground_time_minutes !== null && actual_ground_time_minutes < min_ground_time_minutes && (flightPhase === FLIGHT_STATUS.SCHEDULED || flightPhase === FLIGHT_STATUS.DELAYED || flightPhase === FLIGHT_STATUS.DEPARTED) && (
                                                    <WarningOutlined style={{ marginLeft: 2, color: "#ff4d4f" }} />
                                                )}
                                            </span>
                                        </Tooltip>
                                    ) : null}
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Col>
            </Row>
        );
    };

    // 构建悬浮卡片内容 - 更完整的航班时间轴视图
    const popoverContent = (
        <div style={{ width: 480 }}>
            {/* 航班基本信息 - 使用 phaseInfo */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <Space>
                    <span style={{ fontWeight: "bold" }}>
                        {flight.flight_number} ({departure_airport} → {arrival_airport}, {flight.flight_date ? dayjs.utc(flight.flight_date).format("D-MMM") : "未知"})
                    </span>
                    {stc_type && (
                        <Tag color="cyan" style={{ marginLeft: 4 }}>
                            <FlagOutlined /> {stc_type}
                        </Tag>
                    )}
                </Space>
                <Tag color={phaseInfo.color}>
                    {phaseInfo.icon} {phaseInfo.label}
                </Tag>
            </div>

            {/* 航班时间信息卡片 - 优化为表格式布局 */}
            <Card size="small" styles={{ body: { padding: "12px" } }} style={{ marginBottom: 8 }}>
                {/* 表头行 */}
                <Row gutter={[8, 8]}>
                    <Col span={5}></Col>
                    <Col span={8} style={{ fontWeight: "bold", fontSize: "13px", textAlign: "right" }}>
                        起飞 ({departure_airport})
                    </Col>
                    <Col span={8} style={{ fontWeight: "bold", fontSize: "13px", textAlign: "right" }}>
                        到达 ({arrival_airport})
                    </Col>
                    <Col span={3} style={{ fontWeight: "bold", fontSize: "13px", textAlign: "right" }}>
                        间隔
                    </Col>
                </Row>
                <Divider style={{ margin: "8px 0" }} />

                {/* 计划时间行 */}
                <Row gutter={[8, 8]} style={{ fontSize: "12px" }}>
                    <Col span={5} style={{ color: "#1890ff", fontWeight: "500", textAlign: "right" }}>
                        计划
                    </Col>
                    <Col span={8} style={{ textAlign: "right" }}>
                        <FlightTimeDisplay utcTime={std} airportCode={departure_airport} airportsMap={airportsMap} format="MM-DD HH:mm" showTimezone={true} />
                    </Col>
                    <Col span={8} style={{ textAlign: "right" }}>
                        <FlightTimeDisplay utcTime={sta} airportCode={arrival_airport} baseUtcTime={std} airportsMap={airportsMap} showTimezone={true} />
                    </Col>
                    <Col span={3} style={{ textAlign: "right" }}>
                        {std && sta && formatBlockTimeDuration(dayjs(sta).diff(dayjs(std), "minute"))}
                    </Col>
                </Row>

                {/* 预计时间行 - 只有在有值时才显示 */}
                {(etd || eta) && (
                    <Row gutter={[8, 8]} style={{ fontSize: "12px" }}>
                        <Col span={5} style={{ color: "#fa8c16", fontWeight: "500", textAlign: "right" }}>
                            预计
                        </Col>
                        <Col span={8} style={{ textAlign: "right" }}>
                            {etd && (
                                <>
                                    {stdToEtdDiff !== 0 && (
                                        <Tag color={getDelayTagColor(stdToEtdDiff)} size="small" style={{ marginRight: 4, fontSize: "10px", padding: "0 4px", lineHeight: "16px" }}>
                                            {stdToEtdDiff > 0 ? `+${stdToEtdDiff}m` : `${stdToEtdDiff}m`}
                                        </Tag>
                                    )}
                                    <FlightTimeDisplay utcTime={etd} airportCode={departure_airport} airportsMap={airportsMap} stdTime={std} style={{ fontSize: "11px" }} />
                                </>
                            )}
                        </Col>
                        <Col span={8} style={{ textAlign: "right" }}>
                            {eta && (
                                <>
                                    {staToEtaDiff !== 0 && (
                                        <Tag color={getDelayTagColor(staToEtaDiff)} size="small" style={{ marginRight: 4, fontSize: "10px", padding: "0 4px", lineHeight: "16px" }}>
                                            {staToEtaDiff > 0 ? `+${staToEtaDiff}m` : `${staToEtaDiff}m`}
                                        </Tag>
                                    )}
                                    <FlightTimeDisplay utcTime={eta} airportCode={arrival_airport} baseUtcTime={etd || std} airportsMap={airportsMap} style={{ fontSize: "11px" }} />
                                </>
                            )}
                        </Col>
                        <Col span={3} style={{ textAlign: "right" }}>
                            {etd && eta && formatBlockTimeDuration(dayjs(eta).diff(dayjs(etd), "minute"))}
                        </Col>
                    </Row>
                )}

                {/* 实际时间行（降级展示）- 只有在有值时才显示 */}
                {(displayAtd || displayAta) && (
                    <Row gutter={[8, 8]} style={{ fontSize: "12px" }}>
                        <Col span={5} style={{ color: "#52c41a", fontWeight: "500", textAlign: "right" }}>
                            实际
                        </Col>
                        <Col span={8} style={{ textAlign: "right" }}>
                            {displayAtd && (
                                <>
                                    {stdToAtdDiff !== 0 && (
                                        <Tag color={getDelayTagColor(stdToAtdDiff)} size="small" style={{ marginRight: 4, fontSize: "10px", padding: "0 4px", lineHeight: "16px" }}>
                                            {stdToAtdDiff > 0 ? `+${stdToAtdDiff}m` : `${stdToAtdDiff}m`}
                                        </Tag>
                                    )}
                                    <FlightTimeDisplay utcTime={displayAtd} airportCode={departure_airport} airportsMap={airportsMap} format="HH:mm" stdTime={std} style={{ fontSize: "11px" }} />
                                    <Tag color={atdSource === "settlement" ? "blue" : "default"} style={{ fontSize: "8px", lineHeight: "10px", padding: "0 2px", marginLeft: 2 }}>{atdSource === "settlement" ? "结" : "运"}</Tag>
                                </>
                            )}
                        </Col>
                        <Col span={8} style={{ textAlign: "right" }}>
                            {displayAta && (
                                <>
                                    {staToAtaDiff !== 0 && (
                                        <Tag color={getDelayTagColor(staToAtaDiff)} size="small" style={{ marginRight: 4, fontSize: "10px", padding: "0 4px", lineHeight: "16px" }}>
                                            {staToAtaDiff > 0 ? `+${staToAtaDiff}m` : `${staToAtaDiff}m`}
                                        </Tag>
                                    )}
                                    <FlightTimeDisplay utcTime={displayAta} airportCode={arrival_airport} baseUtcTime={displayAtd || std} airportsMap={airportsMap} format="HH:mm" style={{ fontSize: "11px" }} />
                                    <Tag color={ataSource === "settlement" ? "blue" : "default"} style={{ fontSize: "8px", lineHeight: "10px", padding: "0 2px", marginLeft: 2 }}>{ataSource === "settlement" ? "结" : "运"}</Tag>
                                </>
                            )}
                        </Col>
                        <Col span={3} style={{ textAlign: "right" }}>
                            {displayAtd && displayAta && formatBlockTimeDuration(dayjs(displayAta).diff(dayjs(displayAtd), "minute"))}
                        </Col>
                    </Row>
                )}

                {/* 离地/着陆时间行 - 只有在有值时才显示 */}
                {(flight.atd_icao || flight.ata_icao) && (
                    <Row gutter={[8, 8]} style={{ fontSize: "12px" }}>
                        <Col span={5} style={{ color: "#722ed1", fontWeight: "500", textAlign: "right" }}>
                            离地/着陆
                        </Col>
                        <Col span={8} style={{ textAlign: "right" }}>
                            {flight.atd_icao && <FlightTimeDisplay utcTime={flight.atd_icao} airportCode={departure_airport} airportsMap={airportsMap} format="HH:mm" stdTime={std} style={{ fontSize: "11px" }} />}
                        </Col>
                        <Col span={8} style={{ textAlign: "right" }}>
                            {flight.ata_icao && <FlightTimeDisplay utcTime={flight.ata_icao} airportCode={arrival_airport} baseUtcTime={flight.atd_icao} airportsMap={airportsMap} format="HH:mm" style={{ fontSize: "11px" }} />}
                        </Col>
                        <Col span={3} style={{ textAlign: "right" }}>
                            {flight.atd_icao && flight.ata_icao && formatBlockTimeDuration(dayjs(flight.ata_icao).diff(dayjs(flight.atd_icao), "minute"))}
                        </Col>
                    </Row>
                )}
            </Card>

            {/* 飞行信息卡片 */}
            <Card size="small" styles={{ body: { padding: "12px" } }}>
                <Row gutter={[16, 12]}>
                    <Col span={24}>
                        <div style={{ fontWeight: "bold", fontSize: "13px", marginBottom: 4 }}>
                            <RocketOutlined /> 飞行信息
                        </div>
                    </Col>

                    {/* 关键指标 - 使用3列布局 */}
                    <Col span={24}>
                        <Space size="large" style={{ width: "100%", justifyContent: "flex-start", marginBottom: 8, gap: "32px" }}>
                            <div>
                                <span style={{ fontSize: "12px", color: "#666" }}>轮档时间: </span>
                                <span style={{ fontWeight: "bold", fontSize: "13px" }}>{formatBlockTimeDuration(blockTime) || "未知"}</span>
                            </div>

                            {actual_ground_time_minutes !== null && (
                                <div>
                                    <span style={{ fontSize: "12px", color: "#666" }}>过站时间: </span>
                                    <span style={{ fontWeight: "bold", fontSize: "13px" }}>
                                        {formatGroundTimeDuration(actual_ground_time_minutes)}
                                        {actual_ground_time_minutes !== null && min_ground_time_minutes !== null && actual_ground_time_minutes < min_ground_time_minutes && (flightPhase === FLIGHT_STATUS.SCHEDULED || flightPhase === FLIGHT_STATUS.DELAYED || flightPhase === FLIGHT_STATUS.DEPARTED) && (
                                            <Tooltip title={`小于最低要求(${formatGroundTimeDuration(min_ground_time_minutes)})`}>
                                                <WarningOutlined style={{ marginLeft: 2, color: "#ff4d4f" }} />
                                            </Tooltip>
                                        )}
                                        {min_ground_time_minutes !== null && !(actual_ground_time_minutes !== null && min_ground_time_minutes !== null && actual_ground_time_minutes < min_ground_time_minutes) && (
                                            <Tooltip title={`最小过站时间: ${formatGroundTimeDuration(min_ground_time_minutes)}`}>
                                                <InfoCircleOutlined style={{ marginLeft: 2, color: "#1890ff", fontSize: "10px" }} />
                                            </Tooltip>
                                        )}
                                    </span>
                                </div>
                            )}
                        </Space>
                    </Col>

                    {/* 延误信息 */}
                    {(delay_code || delay_reason) && (
                        <Col span={24}>
                            <div
                                style={{
                                    padding: "6px 8px",
                                    background: "#fffbe6",
                                    borderRadius: "4px",
                                    fontSize: "12px",
                                }}
                            >
                                {delay_code && (
                                    <div style={{ marginBottom: delay_reason ? 4 : 0 }}>
                                        <b>延误代码:</b> {delay_code}
                                    </div>
                                )}
                                {delay_reason && (
                                    <div>
                                        <b>延误原因:</b> {delay_reason}
                                    </div>
                                )}
                            </div>
                        </Col>
                    )}

                    {/* 航班状态进度条 - 使用 flightPhase 判断 */}
                    {flightPhase === FLIGHT_STATUS.DEPARTED && (
                        <Col span={24} style={{ marginTop: 8 }}>
                            <div style={{ fontSize: "12px", marginBottom: 4 }}>航班进度</div>
                            <Progress
                                percent={Math.min(100, Math.max(0, (dayjs().diff(dayjs(atd), "minute") / dayjs(sta).diff(dayjs(std), "minute")) * 100))}
                                size="small"
                                showInfo={false}
                                strokeColor={{
                                    from: "#108ee9",
                                    to: "#87d068",
                                }}
                            />
                            <div style={{ fontSize: "10px", color: "#888", textAlign: "center", marginTop: 2 }}>{dayjs().isAfter(dayjs(atd)) && <>已飞行 {dayjs().diff(dayjs(atd), "minute")} 分钟</>}</div>
                        </Col>
                    )}

                    {/* 添加编辑按钮 - 仅在 editable=true 时显示 */}
                    {editable && (
                        <Col span={24} style={{ marginTop: 12, textAlign: "center" }}>
                            <Divider style={{ margin: "8px 0" }} />
                            <PermissionGuard permission="flight_operation:update">
                                <FlightInfoEditor
                                    flight={flight}
                                    triggerComponent={
                                        <Button type="link" size="small" icon={<EditOutlined />}>
                                            编辑航班信息
                                        </Button>
                                    }
                                    onOpenEditor={() => setPopoverVisible(false)}
                                    onSuccess={onFlightInfoEditSuccess}
                                />
                            </PermissionGuard>
                        </Col>
                    )}
                </Row>
            </Card>

            {/* 航班时间版本历史 - 放在底部 */}
            <FlightChangesSummary flightId={id} visible={popoverVisible} flight={flight} airportsMap={airportsMap} />
        </div>
    );

    // 如果是模态框模式，直接返回内容
    if (showAsModal) {
        return popoverContent;
    }

    return (
        <Popover content={popoverContent} title={null} placement="right" trigger="click" open={popoverVisible} onOpenChange={setPopoverVisible}>
            {triggerComponent ? (
                // 如果提供了 triggerComponent，直接使用它
                <div style={{ cursor: "pointer", width: "100%" }}>{triggerComponent}</div>
            ) : (
                // 否则，使用默认的紧凑时间显示
                <div
                    className="enhanced-flight-times"
                    style={{
                        cursor: "pointer",
                        width: "100%",
                    }}
                >
                    {getPrimaryTimeDisplay()}
                </div>
            )}
        </Popover>
    );
};

export default EnhancedFlightTimes;
